import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { Archive, ArchiveRestore, ChevronDown } from "lucide-react";
import { Suspense, useState } from "react";
import { classifyId } from "shared";
import { toast } from "sonner";
import { SheetSkeleton } from "@/components/skeletons/sheet-skeleton";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { DownloadBudgetReportAction } from "./-components/download-budget-report";

export const Route = createFileRoute("/_authed/_org/budgets/$groupId")({
  beforeLoad: ({ params: { groupId } }) => {
    if (classifyId(groupId) !== "budget_group") {
      throw redirect({
        to: "/budgets",
      });
    }
  },
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc }, params: { groupId } }) => {
    queryClient.ensureQueryData(trpc.budget.get.queryOptions({ groupId }));
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const [archiveAlertOpen, setArchiveAlertOpen] = useState(false);
  const { groupId } = Route.useParams();

  const { queryClient, trpc } = Route.useRouteContext();
  const location = useLocation();
  const { bookkeeper } = useRequiredRole();

  const budget = useSuspenseQuery(
    trpc.budget.get.queryOptions({ groupId }),
  ).data;

  const handleInvalidations = () => {
    queryClient.invalidateQueries(trpc.budget.get.queryOptions({ groupId }));
    queryClient.invalidateQueries(trpc.budget.list.queryOptions());
  };

  const { mutate: archive } = useMutation(
    trpc.budget.archive.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        handleInvalidations();
        toast.info("Budget Archived", {
          position: "bottom-left",
        });
      },
    }),
  );

  const { mutate: unarchive } = useMutation(
    trpc.budget.unArchive.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        handleInvalidations();
        toast.info("Budget Unarchived", {
          position: "bottom-left",
        });
      },
    }),
  );

  return (
    <>
      <SheetHeader className="border-b">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <SheetTitle>{budget.name}</SheetTitle>

            <RenderStatusBadge className="mr-auto" status={budget.status} />
          </div>

          {bookkeeper && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="mr-8">
                <Button size="sm" variant="outline">
                  Actions
                  <ChevronDown
                    aria-hidden="true"
                    className="-me-1 ms-2 opacity-60"
                    size={16}
                    strokeWidth={2}
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {budget.status === "archived" ? (
                  <DropdownMenuItem onClick={() => unarchive({ groupId })}>
                    <ArchiveRestore
                      aria-hidden="true"
                      className="opacity-60"
                      size={16}
                      strokeWidth={2}
                    />
                    Unarchive Budget
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => setArchiveAlertOpen(true)}>
                    <Archive
                      aria-hidden="true"
                      className="opacity-60"
                      size={16}
                      strokeWidth={2}
                    />
                    Archive Budget
                  </DropdownMenuItem>
                )}

                <DownloadBudgetReportAction budget={budget} />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <AlertDialog onOpenChange={setArchiveAlertOpen} open={archiveAlertOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive "{budget.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                This will prevent new transactions from being assigned to this
                budget until is is unarchived.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="justify-between">
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button
                onClick={() => {
                  archive({ groupId });
                  setArchiveAlertOpen(false);
                }}
                variant={"destructive"}
              >
                <Archive />
                Archive "{budget.name}"
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Tabs
          defaultValue="overview"
          onValueChange={(value) =>
            navigate({
              to: value,
            })
          }
          value={location.href}
        >
          <TabsList className="h-auto gap-4 rounded-none bg-transparent px-0 text-foreground">
            <TabsTrigger value={`/budgets/${groupId}`}>Overview</TabsTrigger>

            {bookkeeper && (
              <>
                <TabsTrigger value={`/budgets/${groupId}/reporting`}>
                  Reporting
                </TabsTrigger>
                <TabsTrigger value={`/budgets/${groupId}/edit`}>
                  Edit
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </Tabs>
      </SheetHeader>

      <div className="grow overflow-y-auto pb-4">
        <Suspense
          fallback={
            <div className="px-4">
              <SheetSkeleton />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </div>
    </>
  );
}
